import { useState, useRef, useEffect } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { generatePreviewDates, getWeekdayOccurrenceLabel } from '../../utils/recurringEvents';
import type { RecurrencePattern } from '../../utils/recurringEvents';
import { Link, useNavigate } from 'react-router-dom';
import {
  useTeamEvents,
  useCreateTeamEvent,
  useDeleteTeamEvent,
  usePublishTeamEvent,
  useParticipate,
  useUnparticipate,
  useDecline,
  useEventTypesDropdown,
  useEventGallery,
} from '../../hooks/useTeamEvents';
import { useAdminUsers } from '../../hooks/useAdmin';
import { useAuth } from '../../services/authContext';
import { Button } from '../../components/common/Button';
import { Modal, ConfirmModal } from '../../components/common/Modal';
import { Spinner } from '../../components/common/Spinner';
import { TimeSelect } from '../../components/common/TimeSelect';
import { SpinWheelModal } from '../../components/team-events/SpinWheelModal';
import { formatName, formatLocalDate, formatDisplayDate } from '../../utils/format';
import { formatWeekdayShort } from '../../utils/dateFormat';
import { downloadEventIcs } from '../../utils/icsExport';
import TeamEventCalendar from '../../components/hr/TeamEventCalendar';
import { DateField } from '../../components/ui/DateField';
import { useFlag } from '../../services/flagsContext';
import { MAX_EVENT_GUESTS, type TeamEventListItem } from '../../services/api/teamEvents';
import { clampMaxGuestsPerPerson } from '../../utils/teamEventGuests';

export default function TeamEventsPage() {
  const navigate = useNavigate();
  const { user, isAdmin, isHR } = useAuth();
  const externalGuestsOn = useFlag('mks_533_team_event_external_guests');
  const { data: result, isLoading } = useTeamEvents();
  const participateMutation = useParticipate();
  const unparticipateMutation = useUnparticipate();
  const declineMutation = useDecline();
  const deleteMutation = useDeleteTeamEvent();
  const publishMutation = usePublishTeamEvent();

  const { data: galleryEvents } = useEventGallery();

  const [showCreate, setShowCreate] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [horizonMonths, setHorizonMonths] = useState(3);

  const canSeePastEvents = isAdmin || isHR;
  const today = formatLocalDate(new Date());
  const horizonDate = (() => {
    const d = new Date();
    d.setMonth(d.getMonth() + horizonMonths);
    return formatLocalDate(d);
  })();
  const allEvents = (result?.data ?? []).filter((e) =>
    (canSeePastEvents && showPastEvents) || e.eventDate >= today
  );
  const events = allEvents.filter((e) => e.eventDate <= horizonDate || (showPastEvents && e.eventDate < today));
  const hasMore = allEvents.length > events.length;
  const isMutating = participateMutation.isPending || unparticipateMutation.isPending || declineMutation.isPending;

  // MKS-533 — monks plus the guests they bring. While the flag is off the
  // guests stay out of the count, so the column reads exactly as it did.
  const headcountOf = (event: TeamEventListItem) =>
    event.participantCount + (externalGuestsOn ? event.guestCount : 0);

  const handleDelete = async () => {
    if (deleteId === null) return;
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync(deleteId);
      setDeleteId(null);
    } catch (err) {
      setDeleteError((err as Error).message);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-8"><Spinner /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-semibold text-gray-900">Team Events</h1>
          {canSeePastEvents && (
            <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={showPastEvents}
                onChange={(e) => setShowPastEvents(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Show past events
            </label>
          )}
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)}>New Event</Button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created By</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">MCs</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Participants</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Days to register</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {events.length === 0 && (
              <tr>
                <td colSpan={9} className="px-6 py-8 text-center text-sm text-gray-500">
                  No team events yet. Create one!
                </td>
              </tr>
            )}
            {events.map((event) => {
              const isEventOrganizer = event.organizers.some((o) => o.id === user?.id);
              const canDelete = event.createdBy.id === user?.id || isEventOrganizer || isAdmin || isHR;
              const isPast = event.eventDate < formatLocalDate(new Date());
              const daysToDeadline = (() => {
                if (isPast) return null;
                const today = new Date();
                today.setUTCHours(0, 0, 0, 0);
                const deadline = new Date(event.eventDate + 'T00:00:00Z');
                deadline.setUTCDate(deadline.getUTCDate() - (event.rsvpDeadlineDays ?? 0));
                return Math.ceil((deadline.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
              })();
              const rsvpClosed = daysToDeadline !== null && daysToDeadline <= 0;
              const rsvpUrgent = daysToDeadline !== null && daysToDeadline > 0 && daysToDeadline <= 2
                && !event.isParticipating && !event.isDeclined && event.status === 'published';
              return (
                <tr key={event.id} onClick={() => navigate(`/hr/team-events/${event.id}`)} className={`${event.status === 'cancelled' ? 'bg-red-50 opacity-60' : isPast ? 'bg-gray-50 text-gray-400' : 'hover:bg-gray-50'} ${rsvpUrgent ? 'rsvp-urgent' : ''} cursor-pointer`}>
                  <td className={`px-6 py-3 text-sm whitespace-nowrap ${isPast ? 'text-gray-400' : 'text-gray-900'}`}>
                    {formatDisplayDate(event.eventDate)}
                    {event.eventTime && <span className="text-gray-500 ml-1">{event.eventTime}</span>}
                  </td>
                  <td className="px-6 py-3 text-sm">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${isPast ? 'bg-gray-50 text-gray-400 border-gray-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                      {event.eventType.name}
                    </span>
                    {event.status === 'draft' && (
                      <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-50 text-yellow-800 border border-yellow-200">
                        Draft
                      </span>
                    )}
                  </td>
                  <td className={`px-6 py-3 text-sm ${isPast ? 'text-gray-400' : 'text-gray-900'}`}>
                    {event.title || '—'}
                    {event.seriesId && (
                      <ArrowPathIcon
                        className="inline h-4 w-4 text-gray-400 ml-1 align-middle"
                        aria-label="Recurring event"
                        title="Recurring event"
                      />
                    )}
                    {event.status === 'cancelled' && (
                      <span className="ml-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-200">
                        Cancelled
                      </span>
                    )}
                    {event.description && (
                      <p className="text-xs text-gray-500 mt-0.5 truncate max-w-xs">{event.description}</p>
                    )}
                  </td>
                  <td className={`px-6 py-3 text-sm ${isPast ? 'text-gray-400' : 'text-gray-600'}`}>{formatName(event.createdBy)}</td>
                  <td className={`px-6 py-3 text-sm ${isPast ? 'text-gray-400' : 'text-gray-600'}`}>
                    {event.organizers.map((o) => formatName(o)).join(', ') || '—'}
                  </td>
                  <td className="px-6 py-3 text-sm text-center" onClick={(e) => e.stopPropagation()}>
                    <Link
                      to={`/hr/team-events/${event.id}`}
                      className="text-primary-600 hover:text-primary-800 font-medium"
                    >
                      {/* MKS-533 — guests are heads too; `guestCount` is 0 unless the
                          event is open to them, so this reads as before otherwise. */}
                      {event.eventType.maxParticipants
                        ? `${headcountOf(event)} / ${event.eventType.maxParticipants}`
                        : headcountOf(event)}
                    </Link>
                  </td>
                  <td className="px-6 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                    {event.status === 'cancelled' ? (
                      <span className="text-xs text-red-700 font-semibold">Cancelled</span>
                    ) : event.status === 'draft' ? (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-50 text-yellow-800">
                        Draft
                      </span>
                    ) : isPast || rsvpClosed ? (
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        event.isParticipating ? 'bg-gray-50 text-gray-400' : event.isDeclined ? 'bg-gray-50 text-gray-400' : 'bg-gray-50 text-gray-400'
                      }`}>
                        {event.isParticipating ? (isPast ? '✓ Went' : '✓ Going') : event.isDeclined ? (isPast ? '✗ Declined' : '✗ Not Going') : rsvpClosed ? 'RSVP Closed' : '—'}
                      </span>
                    ) : (() => {
                      const isFull = event.eventType.maxParticipants !== null &&
                        headcountOf(event) >= event.eventType.maxParticipants &&
                        !event.isParticipating;
                      return (
                        <div className="inline-flex gap-1">
                          {event.isParticipating ? (
                            <button
                              onClick={() => unparticipateMutation.mutate(event.id)}
                              disabled={isMutating}
                              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors bg-green-100 text-green-800 hover:bg-green-200"
                            >
                              ✓ Going
                            </button>
                          ) : event.isDeclined ? (
                            <button
                              onClick={() => participateMutation.mutate(event.id)}
                              disabled={isMutating}
                              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors bg-red-100 text-red-800 hover:bg-red-200"
                            >
                              ✗ Not Going
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={() => participateMutation.mutate(event.id)}
                                disabled={isMutating || isFull}
                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                  isFull
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-gray-100 text-gray-600 hover:bg-green-100 hover:text-green-800'
                                }`}
                              >
                                {isFull ? 'Full' : 'Join'}
                              </button>
                              {!isFull && (
                                <button
                                  onClick={() => declineMutation.mutate(event.id)}
                                  disabled={isMutating}
                                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors bg-gray-100 text-gray-600 hover:bg-red-100 hover:text-red-800"
                                >
                                  Not Going
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-3 text-sm text-center">
                    {daysToDeadline !== null && daysToDeadline > 0 ? (
                      <span className={`font-medium ${daysToDeadline <= 2 ? 'text-red-600' : daysToDeadline <= 5 ? 'text-amber-600' : 'text-gray-600'}`}>
                        {daysToDeadline}d
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-right space-x-3" onClick={(e) => e.stopPropagation()}>
                    {event.seriesId !== null ? (
                      <a
                        href={`/api/team-events/${event.id}/ics`}
                        download
                        onClick={(e) => e.stopPropagation()}
                        title="Download series calendar"
                        className="text-gray-500 hover:text-gray-800 text-sm font-semibold"
                      >
                        📅
                      </a>
                    ) : (
                      <button
                        onClick={() => downloadEventIcs({
                          id: event.id,
                          title: event.title,
                          eventTypeName: event.eventType.name,
                          eventDate: event.eventDate,
                          eventTime: event.eventTime,
                          endDate: event.endDate,
                          endTime: event.endTime,
                          location: event.location,
                          videoCallUrl: event.videoCallUrl,
                          description: event.description,
                        })}
                        title="Add to calendar"
                        className="text-gray-500 hover:text-gray-800 text-sm font-medium"
                      >
                        📅
                      </button>
                    )}
                    {canDelete && event.status === 'draft' && (
                      <button
                        onClick={() => publishMutation.mutate(event.id)}
                        disabled={publishMutation.isPending}
                        className="text-green-600 hover:text-green-800 text-sm font-medium"
                      >
                        Publish
                      </button>
                    )}
                    {canDelete && event.status !== 'cancelled' && (
                      <button
                        onClick={() => { setDeleteId(event.id); setDeleteError(null); }}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div className="flex justify-center">
          <button
            onClick={() => setHorizonMonths((m) => m + 3)}
            className="text-sm text-primary-600 hover:text-primary-800 font-medium"
          >
            Show more events
          </button>
        </div>
      )}

      {showCreate && (
        <CreateEventModal onClose={() => setShowCreate(false)} />
      )}

      <ConfirmModal
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Event"
        message="Are you sure you want to delete this event? All participant data will be lost."
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
        error={deleteError}
      />

      {/* Photo Gallery */}
      {galleryEvents && galleryEvents.length > 0 && (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-900">Photo Gallery</h2>
          {galleryEvents.map((ge) => (
            <div key={ge.eventId} className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <h3 className="text-lg font-medium text-gray-900">{ge.eventName}</h3>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                  {ge.eventTypeName}
                </span>
                <span className="text-sm text-gray-500">{formatDisplayDate(ge.eventDate)}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {ge.photos.map((photo) => (
                  <a
                    key={photo.id}
                    href={photo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative rounded-lg overflow-hidden bg-gray-100 block"
                  >
                    <img
                      src={photo.url}
                      alt={photo.fileName}
                      className="w-full h-40 object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-3 py-2">
                      <p className="text-white text-sm font-medium drop-shadow-lg">
                        {ge.eventName}
                      </p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Event Calendar */}
      {(result?.data ?? []).length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-900">Event Calendar</h2>
          <TeamEventCalendar events={result?.data ?? []} isAdmin={isAdmin} />
        </div>
      )}
    </div>
  );
}

function VideoCallField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [watching, setWatching] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // F1-004: clearInterval cancels future ticks but cannot cancel a tick
  // already awaiting clipboard.readText() — if that promise resolves after
  // the user toggled the watcher off or the component unmounted, the
  // setters would overwrite a manually-typed URL. Capture a cancelled
  // flag in the effect closure and bail before calling setters. Mirror
  // of the F21-005 fix in TeamEventDetailPage.
  useEffect(() => {
    if (!watching) return;
    let cancelled = false;
    intervalRef.current = setInterval(async () => {
      try {
        const text = await navigator.clipboard.readText();
        if (cancelled) return;
        if (/https:\/\/meet\.google\.com\/[a-z]{3}-[a-z]{4}-[a-z]{3}/.test(text)) {
          onChange(text);
          setWatching(false);
        }
      } catch { /* clipboard permission denied */ }
    }, 1000);
    return () => {
      cancelled = true;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [watching, onChange]);

  const handleGenerate = () => {
    window.open('https://meet.google.com/landing', '_blank');
    setWatching(true);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">Video Call URL</label>
        <button type="button" onClick={handleGenerate} className="text-xs text-primary-600 hover:text-primary-800 font-medium">
          {watching ? '⏳ Waiting for clipboard…' : 'Create Google Meet link'}
        </button>
      </div>
      <input
        type="url"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. https://meet.google.com/abc-defg-hij"
        className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
      />
    </div>
  );
}

function CreateEventModal({ onClose }: { onClose: () => void }) {
  const { data: eventTypes, isLoading: typesLoading } = useEventTypesDropdown();
  const { data: allUsers } = useAdminUsers();
  const createMutation = useCreateTeamEvent();

  const [eventTypeId, setEventTypeId] = useState<number | ''>('');
  const [eventDate, setEventDate] = useState('');
  const [eventTime, setEventTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [videoCallUrl, setVideoCallUrl] = useState('');
  const [organizerIds, setOrganizerIds] = useState<number[]>([]);
  const [rsvpDeadlineDays, setRsvpDeadlineDays] = useState<string>('');
  const [isInviteOnly, setIsInviteOnly] = useState(false);
  const [allowExternalGuests, setAllowExternalGuests] = useState(false);
  // MKS-540 — how many guests each person may bring. One by default: a plus-one
  // is the common case, and raising it is a deliberate act.
  const [maxGuestsPerPerson, setMaxGuestsPerPerson] = useState(1);
  const [recurrencePattern, setRecurrencePattern] = useState<RecurrencePattern | ''>('');
  const externalGuestsOn = useFlag('mks_533_team_event_external_guests');
  const [error, setError] = useState<string | null>(null);
  const [showSpinWheel, setShowSpinWheel] = useState(false);

  const previewDates =
    recurrencePattern && eventDate ? generatePreviewDates(recurrencePattern, eventDate, 5) : [];
  const monthlyLabel =
    recurrencePattern === 'monthly' && eventDate ? getWeekdayOccurrenceLabel(eventDate) : '';

  const activeUsers = allUsers?.filter((u) => !u.deletedAt) ?? [];

  const handleAddOrganizer = (userId: number) => {
    if (!organizerIds.includes(userId)) {
      setOrganizerIds([...organizerIds, userId]);
    }
  };

  const handleRemoveOrganizer = (userId: number) => {
    setOrganizerIds(organizerIds.filter((id) => id !== userId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!eventTypeId || !eventDate) return;

    try {
      await createMutation.mutateAsync({
        eventTypeId: Number(eventTypeId),
        eventDate,
        eventTime: eventTime || null,
        endDate: endDate || null,
        endTime: endTime || null,
        title: title || null,
        description: description || null,
        location: location || null,
        videoCallUrl: videoCallUrl || null,
        organizerIds: organizerIds.length > 0 ? organizerIds : undefined,
        rsvpDeadlineDays: rsvpDeadlineDays ? parseInt(rsvpDeadlineDays, 10) : null,
        isInviteOnly: isInviteOnly || undefined,
        allowExternalGuests: externalGuestsOn && allowExternalGuests ? true : undefined,
        maxGuestsPerPerson:
          externalGuestsOn && allowExternalGuests ? maxGuestsPerPerson : undefined,
        recurrencePattern: recurrencePattern || undefined,
      });
      setRecurrencePattern('');
      onClose();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="New Team Event" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <p className="text-sm text-red-600">{error}</p>}

        <div>
          <label className="block text-sm font-medium text-gray-700">Event Type *</label>
          <select
            required
            value={eventTypeId}
            onChange={(e) => {
              const val = e.target.value ? Number(e.target.value) : '';
              setEventTypeId(val);
              if (val && eventTypes) {
                const et = eventTypes.find((t) => t.id === val);
                setRsvpDeadlineDays(et?.rsvpDeadlineDays?.toString() ?? '');
              } else {
                setRsvpDeadlineDays('');
              }
            }}
            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            disabled={typesLoading}
          >
            <option value="">Select type...</option>
            {eventTypes?.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Date *</label>
            <DateField
              required
              value={eventDate}
              onChange={(nextDate) => { setEventDate(nextDate); if (!endDate) setEndDate(nextDate); }}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Time</label>
            <TimeSelect
              value={eventTime}
              onChange={(v) => { setEventTime(v); if (v && !endTime) { const parts = v.split(':').map(Number); const h = parts[0] ?? 0; const m = parts[1] ?? 0; setEndTime(`${String((h + 1) % 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`); } }}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">End Date</label>
            <DateField
              value={endDate}
              onChange={setEndDate}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">End Time</label>
            <TimeSelect
              value={endTime}
              onChange={setEndTime}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        </div>

        <div>
          <label htmlFor="repeat-pattern" className="block text-sm font-medium text-gray-700">Repeat</label>
          <select
            id="repeat-pattern"
            value={recurrencePattern}
            onChange={(e) => setRecurrencePattern(e.target.value as RecurrencePattern | '')}
            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">Does not repeat</option>
            <option value="weekly">Weekly</option>
            <option value="biweekly">Bi-weekly</option>
            <option value="monthly">Monthly (same weekday occurrence)</option>
          </select>
        </div>

        {recurrencePattern && (
          <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3">
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">Next occurrences</p>
            {eventDate ? (
              <>
                <ul className="space-y-1" role="list">
                  {previewDates.map((dateStr) => (
                    <li key={dateStr} className="text-sm text-gray-700">
                      {/* MKS-351 — "Mon 20.11.2026"; the shared util reads the
                          YYYY-MM-DD as a calendar date, so no timeZone pinning. */}
                      {`${formatWeekdayShort(dateStr)} ${formatDisplayDate(dateStr)}`}
                    </li>
                  ))}
                </ul>
                {monthlyLabel && (
                  <p className="text-xs text-gray-500 mt-2">{monthlyLabel}</p>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-400">Select a start date to preview occurrences</p>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700">RSVP Deadline (days before event)</label>
          <input
            type="number"
            min="1"
            value={rsvpDeadlineDays}
            onChange={(e) => setRsvpDeadlineDays(e.target.value)}
            placeholder="No deadline"
            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Optional title"
            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
            rows={5}
            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Location</label>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="e.g. Prague, Karlovo namesti 1"
            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </div>

        <VideoCallField value={videoCallUrl} onChange={setVideoCallUrl} />

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isInviteOnly}
            onChange={(e) => setIsInviteOnly(e.target.checked)}
            className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
          />
          <span className="text-sm font-medium text-gray-700">Invite only — notifications only go to participants</span>
        </label>

        {externalGuestsOn && (
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={allowExternalGuests}
                onChange={(e) => {
                  setAllowExternalGuests(e.target.checked);
                  // MKS-540 — the switch always comes back on at +1.
                  if (e.target.checked) setMaxGuestsPerPerson(1);
                }}
                className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
              />
              <span className="text-sm font-medium text-gray-700">Open to external guests — participants can bring +N</span>
            </label>
            {allowExternalGuests && (
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="max-guests-per-person">
                  Max guests per person
                </label>
                <input
                  id="max-guests-per-person"
                  type="number"
                  min={1}
                  max={MAX_EVENT_GUESTS}
                  value={maxGuestsPerPerson}
                  onChange={(e) => setMaxGuestsPerPerson(clampMaxGuestsPerPerson(Number(e.target.value)))}
                  className="mt-1 block w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            )}
          </div>
        )}


        <div>
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">MCs</label>
            <button
              type="button"
              onClick={() => setShowSpinWheel(true)}
              disabled={!eventTypeId}
              className="text-xs font-medium text-primary-600 hover:text-primary-800 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              Spin Wheel
            </button>
          </div>
          {organizerIds.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {organizerIds.map((id) => {
                const u = activeUsers.find((u) => u.id === id);
                return (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                  >
                    {u ? formatName(u) : `User #${id}`}
                    <button
                      type="button"
                      onClick={() => handleRemoveOrganizer(id)}
                      className="text-blue-400 hover:text-blue-600"
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          )}
          <select
            value=""
            onChange={(e) => {
              if (e.target.value) handleAddOrganizer(Number(e.target.value));
            }}
            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">Add MC...</option>
            {activeUsers
              .filter((u) => !organizerIds.includes(u.id))
              .map((u) => (
                <option key={u.id} value={u.id}>{formatName(u)}</option>
              ))}
          </select>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" isLoading={createMutation.isPending}>Create</Button>
        </div>
      </form>

      {showSpinWheel && eventTypeId && (
        <SpinWheelModal
          isOpen
          onClose={() => setShowSpinWheel(false)}
          eventTypeId={Number(eventTypeId)}
          excludeUserIds={organizerIds}
          onSelect={(user) => handleAddOrganizer(user.id)}
          eventDate={eventDate || undefined}
        />
      )}
    </Modal>
  );
}

